import { PayloadAction } from '@reduxjs/toolkit';
import { message } from 'antd';
import { AxiosResponse } from 'axios';
import { put, takeLatest } from 'redux-saga/effects';
import {
  fetchAgentList,
  fetchAgentListFailure,
  fetchAgentListSuccess,
  fetchAgentRoute,
  fetchAgentRouteFailure,
  fetchAgentRouteNoDataSuccess,
  fetchAgentRouteSuccess,
  fetchAgentStats,
  fetchAgentStatsFailure,
  fetchAgentStatsSuccess,
  fetchDeviationList,
  fetchDeviationListFailure,
  fetchDeviationListSuccess,
  fetchOnlineAgentList,
  fetchOnlineAgentListFailure,
  fetchOnlineAgentListSuccess,
} from 'redux/slice/field-supervisor/FieldSupervisorDashboard.slice';
import {
  AgentListRequest,
  AgentListResponse,
  AgentRouteRequest,
  AgentRouteResponse,
  DeviationListRequest,
  DeviationListResponse,
} from 'redux/types/FieldSupervisorDashboard.types';
import { fieldSupervisorDashboardEndpoints } from 'services/api/endPoints';
import { HTTPClient } from 'services/api/http-client';

// Saga for fetching agent list
function* fetchAgentListSaga(action: PayloadAction<AgentListRequest>) {
  try {
    const response: AxiosResponse<AgentListResponse> =
      yield HTTPClient.PostRequest(
        fieldSupervisorDashboardEndpoints.GET_AGENT_LIST,
        action.payload
      );
    yield put(fetchAgentListSuccess(response.data));
  } catch (error: any) {
    yield put(fetchAgentListFailure(error?.message));
    message.error('Failed to fetch agent list');
  }
}

// Saga for fetching agent stats (always with statusFilter='ALL')
function* fetchAgentStatsSaga(action: PayloadAction<AgentListRequest>) {
  try {
    const response: AxiosResponse<AgentListResponse> =
      yield HTTPClient.PostRequest(
        fieldSupervisorDashboardEndpoints.GET_AGENT_LIST,
        { ...action.payload, statusFilter: 'ALL' } // Always fetch with statusFilter='ALL'
      );
    yield put(
      fetchAgentStatsSuccess({
        totalAgentCount: response.data.totalAgentCount,
        onlineAgentCount: response.data.onlineAgentCount,
        offlineAgentCount: response.data.offlineAgentCount,
      })
    );
  } catch (error: any) {
    yield put(fetchAgentStatsFailure(error?.message));
  }
}

// Saga for fetching deviation list
function* fetchDeviationListSaga(action: PayloadAction<DeviationListRequest>) {
  try {
    const response: AxiosResponse<DeviationListResponse> =
      yield HTTPClient.PostRequest(
        fieldSupervisorDashboardEndpoints.GET_DEVIATION_LIST,
        action.payload
      );
    yield put(fetchDeviationListSuccess(response.data));
  } catch (error: any) {
    yield put(fetchDeviationListFailure(error?.message));
    message.error('Failed to fetch deviation list');
  }
}

// Saga for fetching ONLINE agent list (for map)
function* fetchOnlineAgentListSaga(action: PayloadAction<AgentListRequest>) {
  try {
    const response: AxiosResponse<AgentListResponse> =
      yield HTTPClient.PostRequest(
        fieldSupervisorDashboardEndpoints.GET_AGENT_LIST, // Uses the same endpoint
        action.payload
      );
    yield put(fetchOnlineAgentListSuccess(response.data));
  } catch (error: any) {
    yield put(fetchOnlineAgentListFailure(error?.message));
    message.error('Failed to fetch online agent list for map');
  }
}

// Saga for fetching agent route
function* fetchAgentRouteSaga(action: PayloadAction<AgentRouteRequest>) {
  try {
    const response: AxiosResponse<AgentRouteResponse> =
      yield HTTPClient.PostRequest(
        fieldSupervisorDashboardEndpoints.GET_AGENT_ROUTE,
        action.payload
      );
    if (response.status === 200) {
      if (response.data === null) {
        yield put(fetchAgentRouteNoDataSuccess());
        message.info('No data found for the agent route');
      } else {
        yield put(fetchAgentRouteSuccess(response.data));
      }
    }
  } catch (error: any) {
    yield put(fetchAgentRouteFailure(error?.message));
    message.error('Failed to fetch agent route');
  }
}

export function* watchFieldSupervisorDashboardSagas() {
  yield takeLatest(fetchAgentList.type, fetchAgentListSaga);
  yield takeLatest(fetchAgentStats.type, fetchAgentStatsSaga); // Use takeLatest to ensure stats always complete
  yield takeLatest(fetchDeviationList.type, fetchDeviationListSaga);
  yield takeLatest(fetchOnlineAgentList.type, fetchOnlineAgentListSaga);
  yield takeLatest(fetchAgentRoute.type, fetchAgentRouteSaga);
}
